import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';

import {
  InputUI,
  AutoCompleteSelectUI,
  FormErrorUI,
  FormLabelUI,
  RadioGroupUI,
  CheckboxUI,
  SelectUI,
} from '../Interface';
import {
  InvManagementMethodList,
  ItemGroupType,
  ItemGroupTypeList,
  StockArticleTypeList,
} from '../../config/itemGroup';
import useConfig from '../../context/ConfigContext';

export const ItemFields = ({ viewField, selectedValues, autocompleteOptions, addField, autoCompleteData }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  const handlechangeVatCode = (itemType) => {
    const { purchaseVatCodeService, purchaseVatCodeItem, salesVatCodeService, salesVatCodeItem } = config;
    if (itemType === ItemGroupType.SERVICE) {
      setFieldValue('purchaseVatCode', purchaseVatCodeService || null);
      setFieldValue('salesVatCode', salesVatCodeService || null);
    } else if (itemType === ItemGroupType.STOCKARTICLE) {
      setFieldValue('salesVatCode', salesVatCodeItem || null);
      setFieldValue('purchaseVatCode', purchaseVatCodeItem || null);
    }
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="itemCode"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cikkszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          disabled={viewField || addField}
          InputLabelProps={{ shrink: values.itemCode && values.itemCode !== '' }}
        />
      </div>
      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="itemName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cikknév" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemName && values.itemName !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="itemGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cikkcsoport" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemGroupId} />}
          onChange={(_e, newVal) => {
            setFieldValue('itemGroupId', newVal?.value ?? null).then(() => {
              validateField('itemGroupId');
            });
            const findedGroup = autoCompleteData.itemGroup.find((i) => i.id === newVal?.value);
            const findedME = autoCompleteData.quantityUnit.find((i) => findedGroup?.quantityUnitId === i?.id);
            setFieldValue('weight', findedME?.weight ?? '');
            setFieldValue('quantityUnitId', findedGroup?.quantityUnitId ?? null);
            setFieldValue('type', findedGroup?.type ?? null);
            setFieldValue('landedCost', false);
            setFieldValue('stockItemType', findedGroup?.stockItemType ?? null);
            setFieldValue('inPurchase', findedGroup?.inPurchase ?? null);
            setFieldValue('inSales', findedGroup?.inSales ?? null);

            if (!values.purchaseVatCode || !values.salesVatCode) {
              handlechangeVatCode(findedGroup?.type ?? null);
            }
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemGroupId !== '' }}
          selectedValue={values.itemGroupId}
          selectedLabelValue={selectedValues?.itemGroup?.name}
          dataset={autocompleteOptions?.itemGroup}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="quantityUnitId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Mennyiségi egység" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.quantityUnitId} />}
          onChange={(_e, newVal) => {
            setFieldValue('quantityUnitId', newVal?.value ?? null).then(() => {
              validateField('quantityUnitId');
            });
            const findedME = autoCompleteData.quantityUnit.find((i) => i.id === newVal?.value);
            setFieldValue('weight', findedME?.weight ?? '');
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.quantityUnitId !== '' }}
          selectedValue={values.quantityUnitId}
          selectedLabelValue={selectedValues?.quantityUnit?.name}
          dataset={autocompleteOptions?.quantityUnit}
        />
      </div>
      <div className="col-span-2">
        <Field
          type="radio"
          name="type"
          as={RadioGroupUI}
          row
          label={<FormLabelUI text="Típus" />}
          helperText={<FormErrorUI message={errors.type} />}
          disabled
          value={values.type}
          option={ItemGroupTypeList}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-2 pt-3' : 'hidden'}>
        <Field
          name="stockItemType"
          component={SelectUI}
          fullWidth
          option={StockArticleTypeList}
          value={values.stockItemType ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('stockItemType', value ?? null).then(() => {
              validateField('stockItemType');
            });
          }}
          label={<FormLabelUI text="Készletcikk fajta" />}
          helperText={<FormErrorUI message={errors.stockItemType} />}
          disabled
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          name="inPurchase"
          as={CheckboxUI}
          fullWidth
          checked={values.inPurchase ?? false}
          variant="standard"
          onChange={(e) => {
            setFieldValue('inPurchase', e.target.checked ?? null).then(() => {
              validateField('inPurchase');
            });
          }}
          label={<FormLabelUI text="Beszerzésben részt vesz" />}
          helperText={<FormErrorUI message={errors.inPurchase} />}
          disabled={viewField}
        />
        <Field
          name="inSales"
          as={CheckboxUI}
          fullWidth
          checked={values.inSales ?? false}
          variant="standard"
          onChange={(e) => {
            setFieldValue('inSales', e.target.checked ?? null).then(() => {
              validateField('inSales');
            });
          }}
          label={<FormLabelUI text="Értékesítésben részt vesz" />}
          helperText={<FormErrorUI message={errors.inSales} />}
          disabled={viewField}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-2 pt-3' : 'hidden'}>
        <Field
          name="invManagementMethod"
          component={SelectUI}
          fullWidth
          option={InvManagementMethodList}
          value={values.invManagementMethod ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('invManagementMethod', value ?? null).then(() => {
              validateField('invManagementMethod');
            });
          }}
          label={<FormLabelUI text="Készletkezelés módja" />}
          helperText={<FormErrorUI message={errors.invManagementMethod} />}
          disabled={viewField}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-4 md:col-span-1' : 'hidden'}>
        <Field
          type="number"
          name="weight"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Súly (kg)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.weight} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.weight && values.weight !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="kshClassificationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="KSH besorolás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.kshClassificationId} />}
          onChange={(_e, newVal) => {
            setFieldValue('kshClassificationId', newVal?.value ?? null).then(() => {
              validateField('kshClassificationId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.kshClassificationId !== '' }}
          selectedValue={values.kshClassificationId}
          selectedLabelValue={selectedValues?.kshClassification?.name}
          dataset={autocompleteOptions?.kshClassification}
        />
      </div>
      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="other"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Egyéb" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.other} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.other && values.other !== '' }}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="text"
          name="barCode"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Vonalkód" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.barCode} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.barCode && values.barCode !== '' }}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="text"
          name="drawingNumber"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Rajzszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.drawingNumber} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.drawingNumber && values.drawingNumber !== '' }}
        />
      </div>
    </div>
  );
};
